import {
  watchedUploadColumn,
  type UploadColumn,
  type UploadLayoutDefinition,
} from '@erp/rpc';

import {
  compileLayout,
  mappingHeaders,
  type CompiledColumn,
  type CompiledLayout,
  type LayoutProblem,
} from './compileLayout';
import { mapColumn, type CellProblem, type RowReader } from './mapColumn';
import {
  buildLookupTable,
  headerIndex,
  lookupPrefix,
  splitLayoutHeaders,
  type LookupTable,
} from './lookupSheet';
import {
  UPLOAD_WIDTH,
  cellDecimal,
  cellText,
  columnIndex,
  isBlankRow,
  isErrorCell,
  type ResultsCell,
  type ResultsSheet,
} from './sheetCells';

/**
 * A supplier workbook → the AS400 "Header Cross" upload CSV, driven by that
 * supplier's layout (see the `pricing.uploadLayouts` contract).
 *
 * Output is CSV text with final values, never formulas: no header row, 82
 * columns, CRLF line endings. Decimals are pre-rounded because AS400 chops
 * rather than rounds.
 */

export type { ResultsCell, ResultsSheet } from './sheetCells';
export { columnIndex } from './sheetCells';

/** Why a data row was left out of the upload. */
export type SkipReason =
  /** None of the layout's key columns is filled. */
  | { kind: 'none-filled'; headers: string[] }
  /** A column the layout skips on blank is empty. */
  | { kind: 'blank'; header: string }
  /** A column the layout skips on zero holds 0. */
  | { kind: 'zero'; header: string };

/** A data row the layout leaves out. */
export interface SkippedRow {
  /** Excel row number in the supplier's workbook. */
  sheetRow: number;
  /** The row as read, one cell per workbook header, for showing it back. */
  cells: ResultsCell[];
  reason: SkipReason;
}

export interface RowError {
  sheetRow: number;
  /** The upload column being filled, or null for a bad cell found up front. */
  column: UploadColumn | null;
  header?: string;
  value: string;
  problem: CellProblem | 'error-cell' | 'no-lookup-match' | 'ambiguous-lookup';
}

/** One written upload line: where it came from and its 82 CSV cells. */
export interface UploadLine {
  sheetRow: number;
  cells: string[];
}

export type ResultsToUploadResult =
  | {
      status: 'ok';
      csv: string;
      lines: UploadLine[];
      rowsRead: number;
      rowsWritten: number;
      skipped: SkippedRow[];
    }
  | {
      status: 'invalid';
      /** The layout reads a lookup file that was not provided (its label). */
      missingLookup: string | null;
      missingHeaders: string[];
      layoutProblems: LayoutProblem[];
      rowErrors: RowError[];
    };

type RowIssue = Omit<RowError, 'sheetRow'>;

type RowOutcome =
  | { kind: 'line'; cells: string[] }
  | { kind: 'skipped'; reason: SkipReason }
  | { kind: 'invalid'; issues: RowIssue[] };

function invalid(
  changes: Partial<Extract<ResultsToUploadResult, { status: 'invalid' }>>,
): ResultsToUploadResult {
  return {
    status: 'invalid',
    missingLookup: null,
    missingHeaders: [],
    layoutProblems: [],
    rowErrors: [],
    ...changes,
  };
}

/** Excel error cells (`#N/A`, `#REF!`) in any column the layout reads. */
function errorCells(row: RowReader, headers: string[]): RowIssue[] {
  return headers.flatMap((header) => {
    const cell = row(header);
    return isErrorCell(cell)
      ? [
          {
            column: null,
            header,
            value: cell.error,
            problem: 'error-cell' as const,
          },
        ]
      : [];
  });
}

/** The upload column an entry waits on (see `watchedUploadColumn`), if any. */
function waitsOn(entry: CompiledColumn): string | null {
  return watchedUploadColumn(entry.mapping);
}

/** Fills the line from the row, then the flags that read other upload columns. */
function mapInto(
  entry: CompiledColumn,
  row: RowReader,
  date1904: boolean,
  cells: string[],
  issues: RowIssue[],
): void {
  const mapped = mapColumn(entry.mapping, row, date1904);
  if (mapped.ok) {
    cells[columnIndex(entry.column)] = mapped.value;
  } else {
    const { ok: _ok, ...problem } = mapped;
    issues.push({ column: entry.column, ...problem });
  }
}

/** A column that waits on another: filled only once that column is. */
function mapWaiting(
  entry: CompiledColumn,
  row: RowReader,
  date1904: boolean,
  cells: string[],
  issues: RowIssue[],
): void {
  const watched = waitsOn(entry) ?? '';
  if ((cells[columnIndex(watched)] ?? '') === '') return;
  if (entry.mapping.kind === 'flag') {
    cells[columnIndex(entry.column)] = entry.mapping.value;
    return;
  }
  // Read now, so only now can its cells fail the row.
  const errors = errorCells(row, mappingHeaders(entry.mapping));
  if (errors.length > 0) issues.push(...errors);
  else mapInto(entry, row, date1904, cells, issues);
}

/**
 * Fills the line from the row, then what waits on another upload column: a
 * flag writes its value, and a source is read at all, only when that column is
 * filled — a source left blank can't fail the row.
 */
function mapLine(
  layout: CompiledLayout,
  row: RowReader,
  date1904: boolean,
): RowOutcome {
  const cells = Array.from({ length: UPLOAD_WIDTH }, () => '');
  const issues: RowIssue[] = [];
  const waiting = layout.columns.filter((entry) => waitsOn(entry) !== null);
  for (const entry of layout.columns) {
    if (waitsOn(entry) === null) mapInto(entry, row, date1904, cells, issues);
  }
  for (const entry of waiting) {
    mapWaiting(entry, row, date1904, cells, issues);
  }
  return issues.length > 0
    ? { kind: 'invalid', issues }
    : { kind: 'line', cells };
}

function isZero(cell: ReturnType<RowReader>): boolean {
  return cellDecimal(cell)?.isZero() ?? false;
}

/** Why the layout leaves this row out, or null to write it. */
function skipReason(layout: CompiledLayout, row: RowReader): SkipReason | null {
  const keys = layout.includeWhenAnyFilled;
  if (keys.length > 0 && keys.every((header) => cellText(row(header)) === '')) {
    return { kind: 'none-filled', headers: keys };
  }
  const blank = layout.skipWhenBlank.find(
    (header) => cellText(row(header)) === '',
  );
  if (blank) return { kind: 'blank', header: blank };
  const zero = layout.skipWhenZero.find((header) => isZero(row(header)));
  return zero ? { kind: 'zero', header: zero } : null;
}

function convertRow(
  layout: CompiledLayout,
  row: RowReader,
  date1904: boolean,
  /** Set when the row has no match in the lookup file. */
  lookupMiss: RowIssue | null,
): RowOutcome {
  // An error cell is never read as a blank. The skip rules come first: an
  // error in a column only a written row needs must not block a skipped row.
  const filterHeaders = new Set([
    ...layout.includeWhenAnyFilled,
    ...layout.skipWhenZero,
    ...layout.skipWhenBlank,
  ]);
  const filterErrors = errorCells(row, [...filterHeaders]);
  if (filterErrors.length > 0) return { kind: 'invalid', issues: filterErrors };
  const reason = skipReason(layout, row);
  if (reason) return { kind: 'skipped', reason };
  const errors = errorCells(
    row,
    layout.rowHeaders.filter((header) => !filterHeaders.has(header)),
  );
  if (errors.length > 0) return { kind: 'invalid', issues: errors };
  if (lookupMiss) return { kind: 'invalid', issues: [lookupMiss] };
  return mapLine(layout, row, date1904);
}

function csvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

/** CSV text: quoted only where needed, CRLF after every line. */
export function toCsv(lines: string[][]): string {
  return lines.map((line) => `${line.map(csvField).join(',')}\r\n`).join('');
}

/** Row numbers count the written lines, 1 to N. */
function numberRows(layout: CompiledLayout, lines: UploadLine[]): void {
  const rowNumberColumns: number[] = [];
  for (const { column, mapping } of layout.columns) {
    if (mapping.kind === 'rowNumber')
      rowNumberColumns.push(columnIndex(column));
  }
  lines.forEach((line, index) => {
    for (const column of rowNumberColumns)
      line.cells[column] = String(index + 1);
  });
}

/** Headers the layout reads that a workbook lacks, named as the layout names them. */
function missingColumns(
  wanted: string[],
  available: Map<string, number>,
  prefix = '',
): string[] {
  const missing: string[] = [];
  for (const header of wanted) {
    if (!available.has(header)) missing.push(`${prefix}${header}`);
  }
  return missing;
}

/** Reads a results row's cells, and its matched lookup row's cells by prefix. */
function rowReader(
  cells: ResultsCell[],
  columns: Map<string, number>,
  lookup: { table: LookupTable; row: ResultsCell[] | undefined } | null,
): RowReader {
  return (header) => {
    if (lookup && header.startsWith(lookup.table.prefix)) {
      const column = lookup.table.columns.get(
        header.slice(lookup.table.prefix.length),
      );
      return column === undefined ? undefined : lookup.row?.[column];
    }
    const column = columns.get(header);
    return column === undefined ? undefined : cells[column];
  };
}

function lookupFor(
  table: LookupTable | null,
  cells: ResultsCell[],
  columns: Map<string, number>,
): {
  lookup: { table: LookupTable; row: ResultsCell[] | undefined } | null;
  miss: RowIssue | null;
} {
  if (!table) return { lookup: null, miss: null };
  const matchColumn = columns.get(table.matchColumn);
  const key = cellText(
    matchColumn === undefined ? undefined : cells[matchColumn],
  );
  const row = table.rows.get(key);
  // Two lookup rows for one key: refuse rather than pick one by sheet order.
  const problem = !row
    ? 'no-lookup-match'
    : table.duplicateKeys.has(key)
      ? 'ambiguous-lookup'
      : null;
  const miss: RowIssue | null = problem
    ? { column: null, header: table.matchColumn, value: key, problem }
    : null;
  return { lookup: { table, row }, miss };
}

/**
 * @param lookupSheet the second workbook, for a layout that names one.
 */
export function resultsToUpload(
  sheet: ResultsSheet,
  definition: UploadLayoutDefinition,
  lookupSheet: ResultsSheet | null = null,
): ResultsToUploadResult {
  const compiled = compileLayout(definition);
  if ('problems' in compiled) {
    return invalid({ layoutProblems: compiled.problems });
  }
  const { layout } = compiled;
  const lookup = definition.lookup ?? null;
  if (lookup && !lookupSheet) return invalid({ missingLookup: lookup.label });

  // Header → column index, built once: every cell read goes through it.
  const columns = headerIndex(sheet.headers);
  const table =
    lookup && lookupSheet ? buildLookupTable(lookupSheet, lookup) : null;
  const wanted = splitLayoutHeaders(layout.headers, lookup);
  const missingHeaders = [
    ...missingColumns(wanted.results, columns),
    ...(table && lookup
      ? missingColumns(wanted.lookup, table.columns, lookupPrefix(lookup))
      : []),
  ];
  if (missingHeaders.length > 0) return invalid({ missingHeaders });

  const firstDataRow = (sheet.headerRowNumber ?? 1) + 1;
  const lines: UploadLine[] = [];
  const skipped: SkippedRow[] = [];
  const rowErrors: RowError[] = [];
  let rowsRead = 0;

  sheet.rows.forEach((cells, index) => {
    if (isBlankRow(cells)) return;
    rowsRead += 1;
    const sheetRow = sheet.rowNumbers?.[index] ?? firstDataRow + index;
    const matched = lookupFor(table, cells, columns);
    const row = rowReader(cells, columns, matched.lookup);
    const outcome = convertRow(
      layout,
      row,
      sheet.date1904 ?? false,
      matched.miss,
    );
    if (outcome.kind === 'skipped') {
      skipped.push({ sheetRow, cells, reason: outcome.reason });
    } else if (outcome.kind === 'invalid') {
      rowErrors.push(
        ...outcome.issues.map((issue) => ({ sheetRow, ...issue })),
      );
    } else lines.push({ sheetRow, cells: outcome.cells });
  });

  if (rowErrors.length > 0) return invalid({ rowErrors });
  numberRows(layout, lines);
  return {
    status: 'ok',
    csv: toCsv(lines.map(({ cells }) => cells)),
    lines,
    rowsRead,
    rowsWritten: lines.length,
    skipped,
  };
}
